import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  MapContainer, TileLayer, Rectangle,
  Popup, FeatureGroup, LayersControl, Marker, useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import { EditControl } from "react-leaflet-draw";
import axios from "axios";
import L from "leaflet";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  FaSatellite, FaMapMarkerAlt, FaSearch, FaTimes, FaLeaf, FaTint,
  FaExclamationTriangle, FaCheckCircle, FaSeedling,
  FaCloudRain, FaChartBar, FaFlask, FaLocationArrow,
  FaFilePdf, FaDownload, FaRobot,
} from "react-icons/fa";
import "./CropMonitoring.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png",
});

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

// Smoothly fly the map to a new centre+zoom whenever they change
const MapViewUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom, { duration: 1.2, easeLinearity: 0.25 });
  }, [center, zoom, map]);
  return null;
};

const HEATMAP_COLORS = {
  excellent:       { fill: "#166534", stroke: "#14532d", label: "Excellent",       textColor: "#4ade80", fillOpacity: 0.55 },
  good:            { fill: "#16a34a", stroke: "#15803d", label: "Good Health",     textColor: "#4ade80", fillOpacity: 0.50 },
  healthy:         { fill: "#16a34a", stroke: "#15803d", label: "Good Health",     textColor: "#4ade80", fillOpacity: 0.50 },
  moderate_stress: { fill: "#d97706", stroke: "#b45309", label: "Moderate Stress", textColor: "#fbbf24", fillOpacity: 0.55 },
  moderate:        { fill: "#d97706", stroke: "#b45309", label: "Moderate Stress", textColor: "#fbbf24", fillOpacity: 0.55 },
  high_stress:     { fill: "#c2410c", stroke: "#9a3412", label: "High Stress",     textColor: "#fb923c", fillOpacity: 0.65 },
  warning:         { fill: "#c2410c", stroke: "#9a3412", label: "High Stress",     textColor: "#fb923c", fillOpacity: 0.65 },
  critical:        { fill: "#991b1b", stroke: "#7f1d1d", label: "Critical",        textColor: "#fca5a5", fillOpacity: 0.72 },
};

const LAND_TYPE_COLORS = {
  urban_built:         { fill: "#7f1d1d", stroke: "#450a0a", label: "Urban/Built-up",   textColor: "#fca5a5", fillOpacity: 0.72 },
  barren:              { fill: "#92400e", stroke: "#78350f", label: "Barren Land",       textColor: "#fcd34d", fillOpacity: 0.65 },
  water:               { fill: "#1e40af", stroke: "#1e3a8a", label: "Water Body",        textColor: "#93c5fd", fillOpacity: 0.55 },
  sparse_vegetation:   { fill: "#b45309", stroke: "#92400e", label: "Sparse Veg.",       textColor: "#fde68a", fillOpacity: 0.62 },
  mixed_vegetation:    { fill: "#d97706", stroke: "#b45309", label: "Mixed Veg.",        textColor: "#fbbf24", fillOpacity: 0.55 },
  moderate_vegetation: { fill: "#65a30d", stroke: "#4d7c0f", label: "Moderate Veg.",     textColor: "#bef264", fillOpacity: 0.48 },
  dense_vegetation:    { fill: "#15803d", stroke: "#14532d", label: "Dense Veg./Crops",  textColor: "#4ade80", fillOpacity: 0.45 },
};

const getCellColor = (cell) => {
  const lt = cell?.land_type;
  if (lt && LAND_TYPE_COLORS[lt]) return LAND_TYPE_COLORS[lt];
  const cls = normalizeHealthClass(cell?.health_class);
  return HEATMAP_COLORS[cls] || HEATMAP_COLORS.moderate_stress;
};

const normalizeHealthClass = (cls) => {
  if (!cls) return "moderate_stress";
  const c = cls.toLowerCase().replace(/ /g, "_");
  if (c === "excellent")                        return "excellent";
  if (c === "good" || c === "healthy")          return "good";
  if (c === "moderate_stress" || c === "moderate") return "moderate_stress";
  if (c === "high_stress" || c === "warning")   return "high_stress";
  if (c === "critical")                         return "critical";
  if (c.includes("excellent"))                  return "excellent";
  if (c.includes("good") || c.includes("healthy")) return "good";
  if (c.includes("moderate"))                   return "moderate_stress";
  if (c.includes("high") || c.includes("stress")) return "high_stress";
  if (c.includes("critical"))                   return "critical";
  return "moderate_stress";
};

const GRID_ROWS = ["A","B","C","D","E","F","G","H","I","J"];
const getCellLabel = (index, totalCells) => {
  const cols = Math.round(Math.sqrt(totalCells));
  const row  = Math.floor(index / cols);
  const col  = (index % cols) + 1;
  return `${GRID_ROWS[row] || row}${col}`;
};

const healthBarColor = (score) => {
  if (score >= 75) return "#22c55e";
  if (score >= 55) return "#84cc16";
  if (score >= 35) return "#eab308";
  if (score >= 20) return "#f97316";
  return "#ef4444";
};

const fmt = (v, d = 3) => (v !== null && v !== undefined ? Number(v).toFixed(d) : "N/A");

const Spinner = ({ large }) =>
  large ? <span className="cm-spinner-large" aria-hidden="true" /> : <span className="cm-spinner" aria-hidden="true" />;

const MetricCard = ({ icon, label, value, sub, bg, color }) => (
  <div className="cm-metric-card">
    <div className="cm-metric-icon" style={{ background: bg, color }} aria-hidden="true">{icon}</div>
    <div className="cm-metric-info">
      <div className="cm-metric-label">{label}</div>
      <div className="cm-metric-value">{value}</div>
      {sub && <div className="cm-metric-status">{sub}</div>}
    </div>
  </div>
);

const IndexRow = ({ label, value, min, max, color }) => {
  const pct = Math.round(((value - min) / (max - min)) * 100);
  return (
    <div className="cm-soil-item">
      <span className="cm-soil-label">{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 80, height: 6, background: "#e5e7eb", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: "100%", background: color, borderRadius: 3 }} />
        </div>
        <span className="cm-soil-value">{fmt(value)}</span>
      </div>
    </div>
  );
};

const CropMonitoringPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [latitude, setLatitude]       = useState("");
  const [longitude, setLongitude]     = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSugg, setShowSugg]       = useState(false);
  const [mapCenter, setMapCenter]     = useState([20.5937, 78.9629]);
  const [mapZoom, setMapZoom]         = useState(5);
  const [drawnBbox, setDrawnBbox]     = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [result, setResult]           = useState(null);
  const [selectedCell, setSelectedCell]   = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [searchedLocation, setSearchedLocation] = useState(null);
  const [workflowStep, setWorkflowStep]         = useState("idle");
  const [drawPromptVisible, setDrawPromptVisible] = useState(false);
  const [scanAnim, setScanAnim]                 = useState(false);

  const mapRef          = useRef(null);
  const featureGroupRef = useRef(null);
  const searchRef       = useRef(null);
  const suggTimerRef    = useRef(null);

  const fetchSuggestions = useCallback(async (q) => {
    if (!q || q.length < 3) { setSuggestions([]); return; }
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q + ", India")}&limit=5&addressdetails=1`;
      const res = await fetch(url, { headers: { "User-Agent": "AgroFarming/1.0" } });
      const data = await res.json();
      setSuggestions(data.map((d) => ({ label: d.display_name, lat: parseFloat(d.lat), lon: parseFloat(d.lon), type: d.type })));
      setShowSugg(true);
    } catch { setSuggestions([]); }
  }, []);

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    clearTimeout(suggTimerRef.current);
    suggTimerRef.current = setTimeout(() => fetchSuggestions(v), 350);
  };

  const pickSuggestion = (s) => {
    setSearchQuery(s.label);
    setLatitude(String(s.lat));
    setLongitude(String(s.lon));
    setSuggestions([]);
    setShowSugg(false);
    setSearchedLocation({ lat: s.lat, lon: s.lon, name: s.label });
    setMapCenter([s.lat, s.lon]);
    setMapZoom(17);
    setWorkflowStep("navigated");
    setDrawPromptVisible(true);
    setResult(null);
    setError(null);
    setDrawnBbox(null);
    setSelectedCell(null);
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { setError("Geolocation not supported."); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        setLatitude(String(lat));
        setLongitude(String(lon));
        setSearchQuery(`${lat.toFixed(4)}, ${lon.toFixed(4)}`);
        setSearchedLocation({ lat, lon, name: `${lat.toFixed(4)}, ${lon.toFixed(4)}` });
        setMapCenter([lat, lon]);
        setMapZoom(17);
        setWorkflowStep("navigated");
        setDrawPromptVisible(true);
        setResult(null);
        setError(null);
        setDrawnBbox(null);
        setSelectedCell(null);
        if (featureGroupRef.current) featureGroupRef.current.clearLayers();
      },
      () => setError("Location permission denied."),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const runAnalysis = useCallback(async (payload) => {
    setLoading(true);
    setScanAnim(true);
    setWorkflowStep("analysing");
    setError(null);
    setResult(null);
    setDrawPromptVisible(false);
    try {
      const res = await axios.post(`${API_BASE}/api/crop-health/analyze`, payload, { timeout: 90000 });
      const data = res.data;
      if (!data.success) throw new Error(data.error || "Analysis failed");
      setResult(data);
      setWorkflowStep("done");
      if (data.location && !payload.rectangle) {
        setMapCenter([data.location.lat, data.location.lon]);
        setMapZoom(17);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to analyse. Please try again.");
      setWorkflowStep(searchedLocation ? "navigated" : "idle");
      setDrawPromptVisible(!!searchedLocation);
    } finally {
      setLoading(false);
      setTimeout(() => setScanAnim(false), 600);
    }
  }, [searchedLocation]);

  const handleGoToLocation = (e) => {
    e.preventDefault();
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    const hasCoords = !isNaN(lat) && !isNaN(lon);
    const hasQuery  = searchQuery.trim().length > 0;
    if (!hasCoords && !hasQuery) { setError("Enter a location name or coordinates."); return; }
    setError(null);

    if (hasCoords) {
      setSearchedLocation({ lat, lon, name: searchQuery.trim() || `${lat.toFixed(4)}, ${lon.toFixed(4)}` });
      setMapCenter([lat, lon]);
      setMapZoom(17);
      setWorkflowStep("navigated");
      setDrawPromptVisible(true);
      setResult(null);
      setDrawnBbox(null);
      setSelectedCell(null);
      if (featureGroupRef.current) featureGroupRef.current.clearLayers();
    } else {
      fetchSuggestions(searchQuery.trim());
      setShowSugg(true);
    }
  };

  const onDrawCreated = (e) => {
    const layer = e.layer;
    if (layer && layer.getBounds) {
      const b    = layer.getBounds();
      const sw   = b.getSouthWest();
      const ne   = b.getNorthEast();
      const bbox = [[sw.lat, sw.lng], [ne.lat, ne.lng]];
      setDrawnBbox(bbox);
      setDrawPromptVisible(false);
      const lat = (sw.lat + ne.lat) / 2;
      const lon = (sw.lng + ne.lng) / 2;
      const payload = { latitude: lat, longitude: lon, rectangle: bbox };
      if (searchQuery.trim()) payload.location = searchQuery.trim();
      runAnalysis(payload);
    }
  };

  const onDrawDeleted = () => {
    setDrawnBbox(null);
    setResult(null);
    setSelectedCell(null);
    setWorkflowStep(searchedLocation ? "navigated" : "idle");
    setDrawPromptVisible(!!searchedLocation);
  };

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSugg(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const sat      = result?.satellite    || {};
  const health   = result?.health       || {};
  const loc      = result?.location     || {};
  const heatmap  = result?.heatmap      || [];
  const ndviAI   = result?.ndvi_analysis || null;
  const detectedCrops    = result?.detected_crops    || [];
  const dominantLandType = result?.dominant_land_type || null;
  const landTypeCounts   = result?.land_type_counts   || {};
  const irrigation       = result?.irrigation         || null;

  const generatePDFReport = useCallback(async () => {
    if (!result) return;
    setReportLoading(true);
    try {
      const sat      = result.satellite      || {};
      const health   = result.health         || {};
      const loc      = result.location       || {};
      const hm       = result.heatmap        || [];
      const ndviAI   = result.ndvi_analysis  || null;
      const crops    = result.detected_crops || [];
      const irrInfo  = result.irrigation     || null;
      const locCtx   = result.location_context || "agricultural";
      const now    = new Date();
      const dateStr = now.toLocaleDateString("en-IN", { day:"2-digit", month:"long", year:"numeric" });
      const timeStr = now.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" });

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const margin = 14;
      let y = 0;

      const hex2rgb = (hex) => {
        const r = parseInt(hex.slice(1,3),16);
        const g = parseInt(hex.slice(3,5),16);
        const b = parseInt(hex.slice(5,7),16);
        return [r,g,b];
      };
      const scoreColor = (s) =>
        s >= 75 ? "#16a34a" : s >= 55 ? "#84cc16" : s >= 35 ? "#eab308" : s >= 20 ? "#f97316" : "#ef4444";
      const clsColor = (cls) => ({
        excellent:"#16a34a", good:"#22c55e", healthy:"#22c55e",
        moderate_stress:"#eab308", moderate:"#eab308",
        high_stress:"#f97316", warning:"#f97316", critical:"#ef4444",
      }[cls] || "#6b7280");
      const clsLabel = (cls) => ({
        excellent:"Excellent", good:"Healthy", healthy:"Healthy",
        moderate_stress:"Moderate Stress", moderate:"Moderate Stress",
        high_stress:"High Stress", warning:"High Stress", critical:"Critical",
      }[cls] || cls);

      const addPage = () => { doc.addPage(); y = margin; };
      const checkY = (needed = 20) => { if (y + needed > H - 14) addPage(); };

      const sectionHeader = (num, title) => {
        checkY(16);
        doc.setFillColor(15, 76, 42);
        doc.rect(margin, y, W - margin * 2, 7, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text(`${num}. ${title}`, margin + 4, y + 5);
        y += 11;
      };

      // Cover page
      doc.setFillColor(15, 76, 42);
      doc.rect(0, 0, W, 52, "F");
      doc.setFillColor(40, 167, 69);
      doc.rect(0, 52, W, 4, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("CROP HEALTH ANALYSIS REPORT", W / 2, 22, { align: "center" });
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.text("AI-Powered Satellite Vegetation Intelligence", W / 2, 31, { align: "center" });
      doc.setFontSize(9);
      doc.setTextColor(180, 230, 180);
      doc.text(`Generated: ${dateStr}  ·  ${timeStr}`, W / 2, 40, { align: "center" });
      doc.text("Powered by Sentinel-2 · RandomForest + GradientBoosting Ensemble", W / 2, 47, { align: "center" });

      y = 64;

      doc.setFillColor(240, 253, 244);
      doc.roundedRect(margin, y, W - margin * 2, 28, 3, 3, "F");
      doc.setDrawColor(187, 247, 208);
      doc.roundedRect(margin, y, W - margin * 2, 28, 3, 3, "S");
      doc.setTextColor(22, 101, 52);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text("LOCATION", margin + 6, y + 8);
      doc.setTextColor(17, 24, 39);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const locName = loc.name || "Unknown Location";
      const locLines = doc.splitTextToSize(locName, W - margin * 2 - 12);
      doc.text(locLines, margin + 6, y + 15);
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(8);
      doc.text(
        `Lat: ${loc.lat?.toFixed(5) || "—"}  ·  Lon: ${loc.lon?.toFixed(5) || "—"}  ·  Area: ${result.area_hectares || "—"} ha`,
        margin + 6, y + 24
      );
      y += 36;

      const sc = health.health_score || 0;
      const scHex = scoreColor(sc);
      const [sr, sg, sb] = hex2rgb(scHex);
      doc.setFillColor(sr, sg, sb);
      doc.roundedRect(margin, y, (W - margin * 2) / 2 - 4, 30, 3, 3, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text(`${sc}%`, margin + (W - margin * 2) / 4 - 4, y + 18, { align: "center" });
      doc.setFontSize(8);
      doc.text("OVERALL HEALTH SCORE", margin + (W - margin * 2) / 4 - 4, y + 26, { align: "center" });
      doc.setFillColor(249, 250, 251);
      doc.setDrawColor(229, 231, 235);
      doc.roundedRect(margin + (W - margin * 2) / 2 + 4, y, (W - margin * 2) / 2 - 4, 30, 3, 3, "FD");
      doc.setTextColor(sr, sg, sb);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text(health.health_category || "—", W - margin - (W - margin * 2) / 4 + 4, y + 16, { align: "center" });
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(8);
      doc.text("HEALTH CATEGORY", W - margin - (W - margin * 2) / 4 + 4, y + 25, { align: "center" });
      y += 38;

      sectionHeader(1, "CROP HEALTH METRICS");
      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [["Metric", "Value", "Status / Notes"]],
        body: [
          ["Overall Health Score",  `${health.health_score ?? "—"}%`,    health.health_category || "—"],
          ["NDVI (Area Mean)",      `${sat.ndvi ?? "—"}`,                health.vegetation_density || "—"],
          ["EVI",                   `${sat.evi ?? "—"}`,                 "Enhanced Vegetation Index"],
          ["Moisture Level",        `${health.moisture_level ?? "—"}%`,  `Water stress: ${health.water_stress || "—"}`],
          ["Water Stress",          health.water_stress || "—",           `${health.water_stress_pct ?? "—"}% stress`],
          ["Vegetation Density",    health.vegetation_density || "—",     "—"],
          ["Yield Impact",          health.yield_impact || "—",           health.yield_pct !== undefined ? `${health.yield_pct > 0 ? "+" : ""}${health.yield_pct}%` : "—"],
          ["Satellite Source",      sat.source || "Sentinel-2",           `Acquired: ${sat.acquisition_date || "—"}`],
        ],
        headStyles: { fillColor: [26, 122, 60], textColor: 255, fontSize: 8, fontStyle: "bold" },
        bodyStyles: { fontSize: 8, textColor: [55, 65, 81] },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        columnStyles: { 1: { halign: "center", cellWidth: 28 } },
        theme: "grid",
      });
      y = doc.lastAutoTable.finalY + 8;

      sectionHeader(2, "DETECTED CROPS & IRRIGATION STATUS");

      if (crops.length > 0) {
        checkY(10);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(22, 101, 52);
        doc.text("Detected Crops in Selected Area:", margin, y);
        y += 5;
        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [["#", "Crop Name", "Season", "Growing Status", "Confidence", "Notes"]],
          body: crops.map((cr, i) => [
            `#${i+1}`, cr.name, cr.season || "—",
            cr.growing_status || "—", `${cr.confidence}%`, cr.notes || "—",
          ]),
          headStyles: { fillColor: [26, 122, 60], textColor: 255, fontSize: 7, fontStyle: "bold" },
          bodyStyles: { fontSize: 7, textColor: [55, 65, 81] },
          alternateRowStyles: { fillColor: [240, 253, 244] },
          columnStyles: {
            0: { cellWidth: 8, halign: "center" },
            1: { fontStyle: "bold", cellWidth: 38 },
            2: { cellWidth: 18, halign: "center" },
            3: { cellWidth: 24, halign: "center" },
            4: { cellWidth: 18, halign: "center" },
          },
          theme: "grid",
        });
        y = doc.lastAutoTable.finalY + 6;
      }

      if (irrInfo && irrInfo.status !== "Not Applicable") {
        checkY(22);
        const irrColor = irrInfo.status === "Irrigated" ? "#0284c7"
          : irrInfo.status === "Likely Irrigated" ? "#0ea5e9"
          : irrInfo.status === "Rainfed" ? "#92400e" : "#6b7280";
        const [ir, ig, ib] = hex2rgb(irrColor);
        doc.setFillColor(ir, ig, ib, 0.08);
        doc.setDrawColor(ir, ig, ib);
        doc.roundedRect(margin, y, W - margin * 2, 20, 2, 2, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(ir, ig, ib);
        doc.text(`Irrigation: ${irrInfo.status}  (${irrInfo.confidence}% confidence)  |  Method: ${irrInfo.method}`, margin + 5, y + 7);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(55, 65, 81);
        const irrLines = doc.splitTextToSize(irrInfo.notes || "", W - margin * 2 - 10);
        doc.text(irrLines, margin + 5, y + 14);
        y += 26;
      }

      if (hm.length > 0) {
        const cols3 = Math.round(Math.sqrt(hm.length));
        sectionHeader(3, `ZONE-BY-ZONE ANALYSIS  (${cols3}×${cols3} Grid · ${hm.length} Zones)`);

        const zoneRows = hm.map((cell, i) => {
          const cls   = normalizeHealthClass(cell.health_class);
          const ndvi  = Number(cell.ndvi) || 0;
          const adjNdmi = Math.max(-0.5, Math.min(0.5, (ndvi - 0.40) * 0.75));
          const moist = Math.max(0, Math.min(100, (adjNdmi + 0.5) * 100)).toFixed(0);
          const ws    = adjNdmi < -0.20 ? "Severe" : adjNdmi < 0.00 ? "Moderate" : adjNdmi < 0.20 ? "Low" : "None";
          const [cr, cg, cb] = hex2rgb(clsColor(cls));
          return {
            label: getCellLabel(i, hm.length),
            ndvi: cell.ndvi?.toFixed(3) || "—",
            score: `${cell.cell_score?.toFixed(1) || "—"}%`,
            status: clsLabel(cls),
            landType: (cell.land_type || "—").replace(/_/g, " "),
            moisture: `${moist}%`,
            waterStress: ws,
            color: [cr, cg, cb],
          };
        });

        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [["Zone", "NDVI", "Score", "Status", "Land Type", "Moisture", "Water Stress"]],
          body: zoneRows.map(r => [r.label, r.ndvi, r.score, r.status, r.landType, r.moisture, r.waterStress]),
          headStyles: { fillColor: [26, 122, 60], textColor: 255, fontSize: 7, fontStyle: "bold" },
          bodyStyles: { fontSize: 7, textColor: [55, 65, 81] },
          alternateRowStyles: { fillColor: [249, 250, 251] },
          columnStyles: {
            0: { fontStyle: "bold", cellWidth: 12, halign: "center" },
            1: { halign: "center", cellWidth: 16 },
            2: { halign: "center", cellWidth: 16 },
          },
          didParseCell: (data) => {
            if (data.section === "body" && data.column.index === 3) {
              const row = zoneRows[data.row.index];
              if (row) { data.cell.styles.textColor = row.color; data.cell.styles.fontStyle = "bold"; }
            }
          },
          theme: "grid",
        });
        y = doc.lastAutoTable.finalY + 6;

        checkY(16);
        const scores = hm.map(c => c.cell_score || 0);
        const ndvis  = hm.map(c => Number(c.ndvi) || 0);
        const avg    = arr => (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(2);
        const bestI  = scores.indexOf(Math.max(...scores));
        const worstI = scores.indexOf(Math.min(...scores));
        const actionCount = hm.filter(c => ["critical","high_stress"].includes(normalizeHealthClass(c.health_class))).length;
        doc.setFillColor(240, 253, 244);
        doc.setDrawColor(187, 247, 208);
        doc.roundedRect(margin, y, W - margin * 2, 14, 2, 2, "FD");
        doc.setTextColor(22, 101, 52);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7.5);
        doc.text(
          `Avg NDVI: ${avg(ndvis)}   ·   Avg Health: ${avg(scores)}%   ·   Best Zone: ${getCellLabel(bestI, hm.length)}   ·   Worst Zone: ${getCellLabel(worstI, hm.length)}   ·   Action Needed: ${actionCount} zone(s)`,
          W / 2, y + 9, { align: "center" }
        );
        y += 20;
      }

      if (ndviAI) {
        sectionHeader(4, "AI MODEL ANALYSIS  (RandomForest + GradientBoosting Ensemble)");
        autoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [["Model", "Prediction", "Confidence / Score"]],
          body: [
            ["Ensemble (RF + GB)", ndviAI.ensemble_class?.replace(/_/g," ") || "—", `${ndviAI.ensemble_score}% health score`],
            ["RandomForest",       ndviAI.ml_class?.replace(/_/g," ") || "—",       `${ndviAI.ml_confidence}% confidence`],
            ["Physics Threshold",  ndviAI.threshold_class?.replace(/_/g," ") || "—","Index-based rule"],
          ],
          headStyles: { fillColor: [26, 122, 60], textColor: 255, fontSize: 8, fontStyle: "bold" },
          bodyStyles: { fontSize: 8, textColor: [55, 65, 81] },
          alternateRowStyles: { fillColor: [240, 253, 244] },
          theme: "grid",
        });
        y = doc.lastAutoTable.finalY + 6;

        if (ndviAI.per_class_proba) {
          checkY(30);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(55, 65, 81);
          doc.text("Class Probability Distribution:", margin, y);
          y += 5;
          const probEntries = Object.entries(ndviAI.per_class_proba);
          const barW = (W - margin * 2 - 50) / probEntries.length;
          probEntries.forEach(([cls, pct], idx) => {
            const [cr, cg, cb] = hex2rgb(clsColor(normalizeHealthClass(cls)));
            const bx = margin + idx * (barW + 2);
            const maxBarH = 12;
            const barH = Math.max(1, (pct / 100) * maxBarH);
            doc.setFillColor(cr, cg, cb);
            doc.rect(bx, y + maxBarH - barH, barW, barH, "F");
            doc.setTextColor(107, 114, 128);
            doc.setFontSize(6);
            doc.text(`${pct}%`, bx + barW / 2, y + maxBarH + 4, { align: "center" });
            doc.text(cls.replace(/_/g," "), bx + barW / 2, y + maxBarH + 8, { align: "center" });
          });
          y += 22;
        }

        if (ndviAI.trend) {
          checkY(28);
          doc.setFillColor(240, 253, 244);
          doc.setDrawColor(187, 247, 208);
          doc.roundedRect(margin, y, W - margin * 2, 24, 2, 2, "FD");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.setTextColor(22, 101, 52);
          doc.text("Seasonal Trend Analysis", margin + 4, y + 7);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(55, 65, 81);
          doc.text(
            `Zone: ${ndviAI.trend.zone}  ·  Season: ${ndviAI.trend.season}  ·  Expected NDVI: ${ndviAI.trend.expected_ndvi}  ·  Actual: ${sat.ndvi}  ·  Deviation: ${ndviAI.trend.deviation_pct > 0 ? "+" : ""}${ndviAI.trend.deviation_pct}%`,
            margin + 4, y + 14
          );
          doc.setFont("helvetica", "italic");
          doc.setTextColor(21, 128, 61);
          const trendLines = doc.splitTextToSize(ndviAI.trend.trend_label, W - margin * 2 - 8);
          doc.text(trendLines, margin + 4, y + 21);
          y += 30;
        }
      }

      sectionHeader(5, "AGRONOMIC RECOMMENDATIONS");
      const irr  = health.irrigation_recommendations || [];
      const fert = health.fertilizer_suggestions     || [];

      if (irr.length > 0) {
        checkY(10 + irr.length * 7);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(2, 132, 199);
        doc.text("Irrigation Recommendations", margin, y);
        y += 5;
        irr.forEach(r => {
          checkY(8);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(55, 65, 81);
          const lines = doc.splitTextToSize(`• ${r}`, W - margin * 2 - 4);
          doc.text(lines, margin + 2, y);
          y += lines.length * 5;
        });
        y += 3;
      }

      if (fert.length > 0) {
        checkY(10 + fert.length * 7);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(132, 204, 22);
        doc.text("Fertilizer Suggestions", margin, y);
        y += 5;
        fert.forEach(r => {
          checkY(8);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(55, 65, 81);
          const lines = doc.splitTextToSize(`• ${r}`, W - margin * 2 - 4);
          doc.text(lines, margin + 2, y);
          y += lines.length * 5;
        });
        y += 3;
      }

      const risks = health.risk_alerts || [];
      if (risks.length > 0) {
        sectionHeader(6, "RISK ALERTS");
        risks.forEach(alert => {
          checkY(12);
          const alertColor = alert.level === "critical" ? "#ef4444" : alert.level === "warning" ? "#f97316" : "#22c55e";
          const [ar, ag, ab] = hex2rgb(alertColor);
          doc.setFillColor(ar, ag, ab, 0.1);
          doc.setDrawColor(ar, ag, ab);
          const msgLines = doc.splitTextToSize(alert.message, W - margin * 2 - 10);
          const boxH = msgLines.length * 5 + 6;
          doc.roundedRect(margin, y, W - margin * 2, boxH, 2, 2, "FD");
          doc.setTextColor(ar, ag, ab);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(8);
          doc.text(msgLines, margin + 5, y + 5);
          y += boxH + 4;
        });
      }

      sectionHeader(7, "ANALYSIS SUMMARY");
      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [["Parameter", "Value"]],
        body: [
          ["Location",           loc.name || "—"],
          ["Coordinates",        `${loc.lat?.toFixed(5) || "—"}°N, ${loc.lon?.toFixed(5) || "—"}°E`],
          ["Area Analysed",      result.area_hectares ? `${result.area_hectares} ha` : "—"],
          ["Location Context",   locCtx.replace(/_/g, " ")],
          ["Satellite Source",   sat.source || "Sentinel-2"],
          ["Data Acquired",      sat.acquisition_date || "—"],
          ["Analysis Timestamp", result.analysis_timestamp ? new Date(result.analysis_timestamp).toLocaleString("en-IN") : "—"],
          ["Report Generated",   `${dateStr} at ${timeStr}`],
          ["Model Used",         "RandomForest + GradientBoosting + Physics Ensemble v2"],
        ],
        headStyles: { fillColor: [26, 122, 60], textColor: 255, fontSize: 8, fontStyle: "bold" },
        bodyStyles: { fontSize: 8, textColor: [55, 65, 81] },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 50 } },
        theme: "grid",
      });
      y = doc.lastAutoTable.finalY + 8;

      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFillColor(15, 76, 42);
        doc.rect(0, H - 10, W, 10, "F");
        doc.setTextColor(180, 230, 180);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.text("AgroFarming · AI Crop Health Monitor · Sentinel-2 Satellite Analysis", margin, H - 4);
        doc.text(`Page ${p} of ${totalPages}`, W - margin, H - 4, { align: "right" });
      }

      const safeName = (loc.name || "location").replace(/[^a-z0-9]/gi, "_").slice(0, 30);
      doc.save(`CropHealth_Report_${safeName}_${now.toISOString().slice(0,10)}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setReportLoading(false);
    }
  }, [result]);

  const renderCellDetail = (selCell, onClose, variant = "map") => {
    const { cell, label, cls, c } = selCell;
    const ndviVal    = cell.ndvi !== undefined ? Number(cell.ndvi).toFixed(3) : "—";
    const score      = cell.cell_score ?? "—";
    const ndvi       = Number(cell.ndvi) || 0;
    const adjEvi     = Math.max(0, Math.min(0.8, ndvi * 0.72)).toFixed(3);
    const adjNdmi    = Math.max(-0.5, Math.min(0.5, (ndvi - 0.40) * 0.75)).toFixed(3);
    const adjSavi    = Math.max(0, Math.min(0.8, ndvi * 0.92)).toFixed(3);
    const adjBsi     = Math.max(0, Math.min(0.7, 0.32 - ndvi * 0.38)).toFixed(3);
    const moisture   = Math.max(0, Math.min(100, (parseFloat(adjNdmi) + 0.5) * 100)).toFixed(1);
    const ndmiF      = parseFloat(adjNdmi);
    const wsLabel    = ndmiF < -0.20 ? "Severe" : ndmiF < 0.00 ? "Moderate" : ndmiF < 0.20 ? "Low" : "None";
    const wsColor    = wsLabel === "Severe" ? "#ef4444" : wsLabel === "Moderate" ? "#f97316" : wsLabel === "Low" ? "#eab308" : "#22c55e";
    const vegDensity = ndvi >= 0.70 ? "Very Dense" : ndvi >= 0.50 ? "Dense" : ndvi >= 0.30 ? "Moderate" : ndvi >= 0.15 ? "Sparse" : "Bare / Very Sparse";
    const scoreColor = score >= 75 ? "#22c55e" : score >= 55 ? "#84cc16" : score >= 35 ? "#eab308" : score >= 20 ? "#f97316" : "#ef4444";
    const recText    = cls === "excellent"
      ? "Excellent vegetation health. Maintain current practices and monitor for pest pressure."
      : cls === "good" || cls === "healthy"
      ? "Good crop health. Continue regular irrigation and fertilization schedule."
      : cls === "moderate_stress" || cls === "moderate"
      ? "Moderate stress detected. Consider irrigation within 5-7 days and check for nutrient deficiency."
      : cls === "high_stress" || cls === "warning"
      ? "High stress - irrigate within 2-3 days. Apply foliar spray and inspect for disease."
      : "Critical stress - immediate intervention required. Irrigate urgently and consult agronomist.";

    return (
      <div
        className={`cm-cell-detail-panel cm-cell-detail-panel--${variant}`}
        style={{ borderTop: `3px solid ${c.fill}` }}
      >
        <div className="cm-cell-detail-header" style={{ background: c.fill + "18" }}>
          <div className="cm-cell-detail-badge" style={{ background: c.fill, color: "#fff" }}>
            Zone {label}
          </div>
          <div className="cm-cell-detail-title">
            <span className="cm-cell-detail-status" style={{ color: c.fill }}>{c.label}</span>
            <span className="cm-cell-detail-coords">
              {cell.lat?.toFixed(5)}&deg;N, {cell.lon?.toFixed(5)}&deg;E
            </span>
          </div>
          <button className="cm-cell-detail-close" onClick={onClose} aria-label="Close zone detail">
            <FaTimes />
          </button>
        </div>

        <div className="cm-cell-detail-score-row">
          <span className="cm-cell-detail-score-label">Health Score</span>
          <div className="cm-cell-detail-score-track">
            <div className="cm-cell-detail-score-fill" style={{ width: `${score}%`, background: scoreColor }} />
          </div>
          <span className="cm-cell-detail-score-val" style={{ color: scoreColor }}>{score}%</span>
        </div>

        <div className="cm-cell-detail-metrics">
          {[
            { label: "NDVI",     val: ndviVal, bg: "#f0fdf4", color: "#16a34a", icon: <FaLeaf /> },
            { label: "EVI",      val: adjEvi,  bg: "#f0fdf4", color: "#84cc16", icon: <FaLeaf /> },
            { label: "NDMI",     val: adjNdmi, bg: "#f0f9ff", color: "#0284c7", icon: <FaTint /> },
            { label: "SAVI",     val: adjSavi, bg: "#fefce8", color: "#ca8a04", icon: <FaSeedling /> },
            { label: "BSI",      val: adjBsi,  bg: "#fff7ed", color: "#ea580c", icon: <FaChartBar /> },
            { label: "Moisture", val: `${moisture}%`, bg: "#f0f9ff", color: "#0ea5e9", icon: <FaCloudRain /> },
          ].map(({ label: ml, val, bg, color, icon }) => (
            <div key={ml} className="cm-cell-detail-metric">
              <div className="cm-cell-detail-metric-icon" style={{ background: bg, color }}>{icon}</div>
              <div>
                <div className="cm-cell-detail-metric-label">{ml}</div>
                <div className="cm-cell-detail-metric-val">{val}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="cm-cell-detail-info">
          <div className="cm-cell-detail-info-row">
            <span>Vegetation Density</span>
            <strong>{vegDensity}</strong>
          </div>
          <div className="cm-cell-detail-info-row">
            <span>Water Stress</span>
            <strong style={{ color: wsColor }}>{wsLabel}</strong>
          </div>
          <div className="cm-cell-detail-info-row">
            <span>Health Class</span>
            <strong style={{ color: c.fill, textTransform: "capitalize" }}>
              {cell.health_class?.replace(/_/g, " ")}
            </strong>
          </div>
          <div className="cm-cell-detail-info-row">
            <span>Coordinates</span>
            <strong>{cell.lat?.toFixed(5)}, {cell.lon?.toFixed(5)}</strong>
          </div>
        </div>

        <div className="cm-cell-detail-rec" style={{ borderLeft: `3px solid ${c.fill}` }}>
          <div className="cm-cell-detail-rec-title">Zone Recommendation</div>
          <div className="cm-cell-detail-rec-text">{recText}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="crop-monitoring-page">
      <header className="cm-header">
        <div className="cm-header-content">
          <div className="cm-header-icon"><FaSatellite size={28} /></div>
          <h1 className="cm-header-title">Crop Health Monitor</h1>
          <p className="cm-header-subtitle">
            AI-powered satellite analysis using Sentinel-2 imagery &mdash; NDVI, EVI, NDMI and more.
          </p>
        </div>
      </header>

      <div className="cm-body">
        <section className="cm-search-panel" aria-label="Location search">
          <span className="cm-panel-title"><FaSearch size={14} style={{ marginRight: 6 }} />Find a Location</span>

          <form onSubmit={handleGoToLocation} className="cm-search-form" noValidate>
            <div className="cm-search-input-wrap" ref={searchRef} style={{ gridColumn: "1 / -1", position: "relative" }}>
              <FaSearch className="cm-search-icon" />
              <input
                type="text"
                className="cm-search-input"
                placeholder="Search location (e.g. Punjab, Ludhiana, Nashik...)"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => suggestions.length > 0 && setShowSugg(true)}
                aria-label="Location search"
                aria-autocomplete="list"
                aria-expanded={showSugg}
              />
              {searchQuery && (
                <button type="button" className="cm-clear-btn" onClick={() => { setSearchQuery(""); setSuggestions([]); setWorkflowStep("idle"); setDrawPromptVisible(false); }} aria-label="Clear search">
                  <FaTimes />
                </button>
              )}
              {showSugg && suggestions.length > 0 && (
                <ul className="cm-suggestions" role="listbox">
                  {suggestions.map((s, i) => (
                    <li key={i} className="cm-suggestion-item" role="option" onClick={() => pickSuggestion(s)}>
                      <FaMapMarkerAlt className="cm-sugg-icon" />
                      <span>{s.label}</span>
                      <span className="cm-sugg-type">{s.type}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="cm-coords-row">
              <div className="cm-coord-field">
                <label className="cm-coord-label" htmlFor="cm-lat">Latitude</label>
                <input
                  id="cm-lat"
                  type="number"
                  step="any"
                  className="cm-search-input cm-coord-input"
                  placeholder="e.g. 28.6139"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  aria-label="Latitude"
                />
              </div>
              <div className="cm-coord-field">
                <label className="cm-coord-label" htmlFor="cm-lon">Longitude</label>
                <input
                  id="cm-lon"
                  type="number"
                  step="any"
                  className="cm-search-input cm-coord-input"
                  placeholder="e.g. 77.2090"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  aria-label="Longitude"
                />
              </div>
            </div>

            <div className="cm-search-actions">
              <button
                type="button"
                className="cm-btn cm-btn-secondary"
                onClick={useMyLocation}
                disabled={loading}
                aria-label="Use my current location"
              >
                <FaLocationArrow size={12} /> My Location
              </button>
            </div>

            <button
              type="submit"
              className="cm-btn-analyze"
              disabled={loading}
              style={{ gridColumn: "1 / -1" }}
            >
              {loading
                ? <><Spinner /> Analysing...</>
                : <><FaMapMarkerAlt size={15} /> Go to Location</>
              }
            </button>
          </form>

          {error && <div className="cm-error" role="alert"><FaExclamationTriangle style={{ marginRight: 6 }} />{error}</div>}

          {workflowStep === "navigated" && (
            <div className="cm-draw-hint">
              <div className="cm-draw-hint-icon">&#9998;&#65039;</div>
              <div>
                <div className="cm-draw-hint-title">Now draw your area on the map</div>
                <div className="cm-draw-hint-sub">Use the rectangle tool (top-right of map) to select the exact region you want to analyse. Analysis starts automatically after you finish drawing.</div>
              </div>
            </div>
          )}
        </section>

        <section className={`cm-map-container${scanAnim ? " cm-map-container--scanning" : ""}`} aria-label="Satellite map">
          <div className="cm-map-header">
            <h2 className="cm-map-title">
              <FaSatellite size={16} />
              {workflowStep === "idle"      && "Satellite View — Search a location to begin"}
              {workflowStep === "navigated" && `📍 ${searchedLocation?.name?.split(",")[0] || "Location found"} — Now draw your area`}
              {workflowStep === "analysing" && "🔬 Analysing selected area..."}
              {workflowStep === "done"      && "✅ Analysis complete"}
            </h2>
            {workflowStep === "done" && result && (
              <div className="cm-map-header-badge">
                <span style={{ color: healthBarColor(result.health?.health_score || 0) }}>
                  &#10003; {result.health?.health_score}% Health
                </span>
              </div>
            )}
          </div>

          {drawPromptVisible && workflowStep === "navigated" && (
            <div className="cm-draw-overlay">
              <div className="cm-draw-overlay-inner">
                <div className="cm-draw-overlay-icon">
                  <span className="cm-draw-pulse" />
                  &#9998;&#65039;
                </div>
                <div className="cm-draw-overlay-text">
                  <strong>Draw your area</strong>
                  <span>Use the <kbd>&#11088;</kbd> rectangle tool in the top-right corner of the map</span>
                </div>
                <button className="cm-draw-overlay-dismiss" onClick={() => setDrawPromptVisible(false)}>
                  <FaTimes size={12} />
                </button>
              </div>
            </div>
          )}

          {workflowStep === "analysing" && (
            <div className="cm-scan-overlay">
              <div className="cm-scan-line" />
              <div className="cm-scan-grid" />
              <div className="cm-scan-label">
                <Spinner /> Scanning selected area with AI...
              </div>
            </div>
          )}

          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            className="cm-leaflet-map"
            ref={mapRef}
            scrollWheelZoom
            zoomControl
          >
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Satellite">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={28}
                  attribution="Tiles &copy; Esri"
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Street Map">
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                  maxZoom={18}
                />
              </LayersControl.BaseLayer>
              <LayersControl.Overlay checked name="Labels">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={18}
                />
              </LayersControl.Overlay>
            </LayersControl>

            <FeatureGroup ref={featureGroupRef}>
              <EditControl
                position="topright"
                onCreated={onDrawCreated}
                onDeleted={onDrawDeleted}
                draw={{
                  rectangle: { shapeOptions: { color: "#28a745", weight: 2, fillOpacity: 0.15 } },
                  polygon: false,
                  circle: false,
                  circlemarker: false,
                  marker: false,
                  polyline: false,
                }}
                edit={{ edit: false, remove: true }}
              />
            </FeatureGroup>

            <MapViewUpdater center={mapCenter} zoom={mapZoom} />

            {result?.location && (
              <Marker position={[result.location.lat, result.location.lon]}>
                <Popup>
                  <strong>{result.location.name}</strong><br />
                  {result.location.lat.toFixed(4)}, {result.location.lon.toFixed(4)}
                </Popup>
              </Marker>
            )}

            {heatmap.map((cell, i) => {
              const c   = getCellColor(cell);
              const cls = normalizeHealthClass(cell.health_class);
              const bounds = cell.bounds
                ? [[cell.bounds[0][0], cell.bounds[0][1]], [cell.bounds[1][0], cell.bounds[1][1]]]
                : [[cell.lat - 0.00045, cell.lon - 0.0005], [cell.lat + 0.00045, cell.lon + 0.0005]];
              const label      = getCellLabel(i, heatmap.length);
              const isSelected = selectedCell?.index === i;
              const baseOpacity = c.fillOpacity ?? 0.55;
              const fillOp     = isSelected ? Math.min(0.85, baseOpacity + 0.18) : baseOpacity;

              const cellIcon = L.divIcon({
                className: "",
                html: `<div class="cm-cell-overlay${isSelected ? " cm-cell-overlay--selected" : ""}">
                  <span class="cm-cell-badge">${label}</span>
                </div>`,
                iconSize: [0, 0],
                iconAnchor: [0, 0],
              });

              return (
                <React.Fragment key={i}>
                  <Rectangle
                    bounds={bounds}
                    pathOptions={{
                      color:       isSelected ? "#ffffff" : c.stroke,
                      fillColor:   c.fill,
                      fillOpacity: fillOp,
                      weight:      isSelected ? 3 : 1.8,
                      opacity:     1,
                    }}
                    eventHandlers={{
                      click: () => setSelectedCell(
                        selectedCell?.index === i ? null : { cell, label, index: i, cls, c }
                      ),
                    }}
                  />
                  <Marker
                    position={[cell.lat, cell.lon]}
                    icon={cellIcon}
                    interactive={false}
                  />
                </React.Fragment>
              );
            })}
          </MapContainer>

          {heatmap.length > 0 && (
            <div className="cm-map-legend-bar">
              {[
                { fill: "#991b1b", label: "Critical" },
                { fill: "#c2410c", label: "High Stress" },
                { fill: "#d97706", label: "Moderate Stress" },
                { fill: "#16a34a", label: "Good Health" },
                { fill: "#166534", label: "Excellent" },
              ].map(({ fill, label }) => (
                <div key={label} className="cm-map-legend-bar-item">
                  <div className="cm-map-legend-bar-dot" style={{ background: fill }} />
                  {label}
                </div>
              ))}
              <span className="cm-map-legend-hint">Click a zone for details</span>
            </div>
          )}

          {selectedCell && renderCellDetail(selectedCell, () => setSelectedCell(null), "map")}
        </section>

        <section className="cm-data-panel" aria-label="Analysis results">
          {loading && (
            <div className="cm-loading-state" role="status" aria-live="polite">
              <Spinner large />
              <span>Fetching satellite data and running AI analysis...</span>
            </div>
          )}

          {!loading && !result && !error && (
            <div className="cm-loading-state" style={{ color: "#9ca3af" }}>
              {workflowStep === "idle" && <>
                <FaSatellite size={36} style={{ opacity: 0.3 }} />
                <span>Search for a location above to get started.</span>
              </>}
              {workflowStep === "navigated" && <>
                <div className="cm-idle-draw-anim">
                  <span className="cm-idle-draw-icon">&#9998;&#65039;</span>
                  <span className="cm-idle-draw-ring" />
                </div>
                <strong style={{ color: "#1a3c2a", fontSize: 15 }}>Draw your area on the map</strong>
                <span style={{ maxWidth: 340, textAlign: "center", lineHeight: 1.6 }}>
                  Use the rectangle tool in the top-right corner of the map to select the exact region you want to analyse. Analysis will start automatically.
                </span>
              </>}
            </div>
          )}

          {!loading && result && (
            <div className="cm-results-body">

              <div className="cm-result-location-bar">
                <span className="cm-result-loc-text">
                  <FaMapMarkerAlt className="cm-result-loc-icon" />
                  <strong>{loc.name || "Unknown location"}</strong>
                  {loc.lat && <span className="cm-result-coords">{loc.lat.toFixed(4)}N, {loc.lon.toFixed(4)}E</span>}
                  {result.area_hectares && (
                    <span className="cm-result-area">
                      {result.area_hectares} ha {!result.bbox ? "· 100m radius" : ""}
                    </span>
                  )}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className={`cm-source-badge ${sat.source && sat.source.includes("Estimated") ? "cm-source-est" : "cm-source-live"}`}>
                    {sat.source && sat.source.includes("Estimated") ? "🟡 Estimated" : "🟢 Live Satellite"}
                    {sat.acquisition_date && <span className="cm-source-date"> &middot; {sat.acquisition_date}</span>}
                  </span>
                  <button
                    className="cm-report-btn"
                    onClick={generatePDFReport}
                    disabled={reportLoading}
                    title="Download AI-generated PDF report"
                  >
                    {reportLoading
                      ? <><Spinner /> Generating...</>
                      : <><FaFilePdf /> <FaDownload size={11} /> Download AI Report</>
                    }
                  </button>
                </div>
              </div>

              <div className="cm-result-section">
                <h3 className="cm-result-section-title"><FaLeaf /> Overview</h3>

                <div className="cm-metrics-grid">
                  <MetricCard icon={<FaLeaf />}      label="Health Score"  value={`${health.health_score ?? "—"}%`}  sub={health.health_category}                                                    bg="#f0fdf4" color="#16a34a" />
                  <MetricCard icon={<FaChartBar />}  label="NDVI"          value={fmt(sat.ndvi)}                      sub={health.vegetation_density}                                                 bg="#eff6ff" color="#2563eb" />
                  <MetricCard icon={<FaTint />}      label="Moisture"      value={`${health.moisture_level ?? "—"}%`} sub={`Water stress: ${health.water_stress || "—"}`}                             bg="#f0f9ff" color="#0284c7" />
                  <MetricCard icon={<FaSeedling />}  label="Yield Impact"  value={health.yield_pct !== undefined ? `${health.yield_pct > 0 ? "+" : ""}${health.yield_pct}%` : "—"} sub={health.yield_impact} bg="#fefce8" color="#ca8a04" />
                </div>

                <div className="cm-health-bar-section">
                  <span className="cm-health-bar-label"><FaLeaf /> Overall Crop Health</span>
                  <div className="cm-health-bar-track" role="progressbar" aria-valuenow={health.health_score} aria-valuemin={0} aria-valuemax={100}>
                    <div className="cm-health-bar-fill" style={{ width: `${health.health_score || 0}%`, background: healthBarColor(health.health_score || 0) }} />
                  </div>
                  <span className="cm-health-bar-pct">{health.health_score ?? "—"}%</span>
                </div>

                {heatmap.length > 0 && (
                  <div className="cm-legend-row">
                    <span className="cm-legend-label"><FaChartBar /> Heatmap:</span>
                    {[
                      { key: "critical",        fill: "#991b1b", stroke: "#7f1d1d", label: "Critical" },
                      { key: "high_stress",     fill: "#c2410c", stroke: "#9a3412", label: "High Stress" },
                      { key: "moderate_stress", fill: "#d97706", stroke: "#b45309", label: "Moderate Stress" },
                      { key: "good",            fill: "#16a34a", stroke: "#15803d", label: "Good Health" },
                      { key: "excellent",       fill: "#166534", stroke: "#14532d", label: "Excellent" },
                    ].map(({ key, fill, stroke, label }) => (
                      <div key={key} className="cm-legend-item">
                        <div className="cm-legend-dot" style={{ background: fill, border: `2px solid ${stroke}` }} />
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {(detectedCrops.length > 0 || irrigation) && (
                <div className="cm-result-section">
                  <h3 className="cm-result-section-title">
                    <FaSeedling /> Crop &amp; Irrigation Intelligence
                  </h3>

                  <div className="cm-crop-irrigation-grid">
                    {irrigation && irrigation.status !== "Not Applicable" && (() => {
                      const statusColor = irrigation.status === "Irrigated" ? "#0284c7"
                        : irrigation.status === "Likely Irrigated" ? "#0ea5e9"
                        : irrigation.status === "Rainfed" ? "#92400e"
                        : "#6b7280";
                      const statusBg = irrigation.status === "Irrigated" ? "#f0f9ff"
                        : irrigation.status === "Likely Irrigated" ? "#e0f2fe"
                        : irrigation.status === "Rainfed" ? "#fef3c7"
                        : "#f9fafb";
                      const statusIcon = irrigation.status === "Irrigated" ? "💧"
                        : irrigation.status === "Likely Irrigated" ? "💦"
                        : irrigation.status === "Rainfed" ? "🌧️"
                        : "—";
                      return (
                        <div className="cm-irrigation-card" style={{ background: statusBg, borderColor: statusColor + "44" }}>
                          <div className="cm-irrigation-icon">{statusIcon}</div>
                          <div className="cm-irrigation-body">
                            <div className="cm-irrigation-label">Irrigation Status</div>
                            <div className="cm-irrigation-status" style={{ color: statusColor }}>
                              {irrigation.status}
                            </div>
                            <div className="cm-irrigation-method">
                              <strong>Method:</strong> {irrigation.method}
                            </div>
                            <div className="cm-irrigation-notes">{irrigation.notes}</div>
                          </div>
                          <div className="cm-irrigation-conf">
                            <div className="cm-irrigation-conf-val" style={{ color: statusColor }}>
                              {irrigation.confidence}%
                            </div>
                            <div className="cm-irrigation-conf-label">Confidence</div>
                          </div>
                        </div>
                      );
                    })()}

                    {detectedCrops.length > 0 && (
                      <div className="cm-crops-panel">
                        <div className="cm-crops-panel-title">
                          <FaLeaf style={{ color: "#16a34a" }} /> Detected Crops in Selected Area
                        </div>
                        <div className="cm-crops-list">
                          {detectedCrops.map((crop, i) => {
                            const confColor = crop.confidence >= 70 ? "#16a34a"
                              : crop.confidence >= 45 ? "#ca8a04" : "#dc2626";
                            const rank = ["🥇","🥈","🥉"][i] || "•";
                            const statusColor = crop.growing_status === "Active" ? "#16a34a"
                              : crop.growing_status === "Emerging" ? "#ca8a04"
                              : crop.growing_status === "Fallow" || crop.growing_status === "Dormant/Fallow" ? "#6b7280"
                              : "#9ca3af";
                            return (
                              <div key={i} className="cm-crop-row">
                                <span className="cm-crop-rank">{rank}</span>
                                <div className="cm-crop-info">
                                  <div className="cm-crop-name">{crop.name}</div>
                                  <div className="cm-crop-meta">
                                    {crop.season && crop.season !== "—" && (
                                      <span className="cm-crop-tag cm-crop-tag--season">
                                        🌾 {crop.season.charAt(0).toUpperCase() + crop.season.slice(1)}
                                      </span>
                                    )}
                                    {crop.growing_status && crop.growing_status !== "Not Applicable" && (
                                      <span className="cm-crop-tag" style={{ color: statusColor, background: statusColor + "15", border: `1px solid ${statusColor}30` }}>
                                        {crop.growing_status === "Active" ? "🟢" : crop.growing_status === "Emerging" ? "🟡" : "⚪"} {crop.growing_status}
                                      </span>
                                    )}
                                  </div>
                                  <div className="cm-crop-note">{crop.notes}</div>
                                </div>
                                <div className="cm-crop-conf-badge" style={{ background: confColor + "18", color: confColor, border: `1px solid ${confColor}44` }}>
                                  {crop.confidence}%
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {heatmap.length > 0 && (() => {
                const cols = Math.round(Math.sqrt(heatmap.length));
                return (
                  <div className="cm-result-section">
                    <h3 className="cm-result-section-title">
                      <FaChartBar /> Zone-by-Zone Analysis
                      <span className="cm-model-badge">{cols}&times;{cols} Grid &middot; {heatmap.length} Zones</span>
                    </h3>
                    <p className="cm-result-meta">
                      Click any zone card to highlight it on the map. Each zone shows its individual NDVI, health score, and status.
                    </p>

                    <div className="cm-zone-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                      {heatmap.map((cell, i) => {
                        const cls      = normalizeHealthClass(cell.health_class);
                        const c        = getCellColor(cell);
                        const lbl      = getCellLabel(i, heatmap.length);
                        const ndviVal  = cell.ndvi !== undefined ? Number(cell.ndvi).toFixed(3) : "—";
                        const score    = cell.cell_score ?? 0;
                        const isActive = selectedCell?.index === i;
                        const ndvi     = Number(cell.ndvi) || 0;
                        const moisture = Math.max(0, Math.min(100,
                          (Math.max(-0.5, Math.min(0.5, (ndvi - 0.40) * 0.75)) + 0.5) * 100
                        )).toFixed(0);
                        const vegDensity = ndvi >= 0.70 ? "Very Dense"
                          : ndvi >= 0.50 ? "Dense"
                          : ndvi >= 0.30 ? "Moderate"
                          : ndvi >= 0.15 ? "Sparse" : "Bare";

                        const selectedRow  = selectedCell ? Math.floor(selectedCell.index / cols) : -1;
                        const thisRow      = Math.floor(i / cols);
                        const isLastInRow  = (i % cols) === cols - 1;
                        const showDrawer   = selectedCell && thisRow === selectedRow && isLastInRow;

                        return (
                          <React.Fragment key={i}>
                            <div
                              className={`cm-zone-card${isActive ? " cm-zone-card--active" : ""}`}
                              style={{ borderTop: `3px solid ${c.fill}`, "--zone-color": c.fill }}
                              onClick={() => setSelectedCell(
                                isActive ? null : { cell, label: lbl, index: i, cls, c }
                              )}
                              role="button"
                              tabIndex={0}
                              aria-pressed={isActive}
                              onKeyDown={e => e.key === "Enter" && setSelectedCell(
                                isActive ? null : { cell, label: lbl, index: i, cls, c }
                              )}
                            >
                              <div className="cm-zone-card-header">
                                <span className="cm-zone-card-label">{lbl}</span>
                                <span className="cm-zone-card-pill" style={{ background: c.fill + "22", color: c.fill }}>
                                  {c.label}
                                </span>
                              </div>

                              <div className="cm-zone-card-ndvi" style={{ color: c.fill }}>
                                {ndviVal}
                              </div>
                              <div className="cm-zone-card-ndvi-label">NDVI</div>

                              <div className="cm-zone-card-bar-wrap">
                                <div className="cm-zone-card-bar-track">
                                  <div
                                    className="cm-zone-card-bar-fill"
                                    style={{ width: `${score}%`, background: c.fill }}
                                  />
                                </div>
                                <span className="cm-zone-card-score" style={{ color: c.fill }}>{score}%</span>
                              </div>

                              <div className="cm-zone-card-stats">
                                <div className="cm-zone-card-stat">
                                  <FaTint size={9} />
                                  <span>{moisture}%</span>
                                </div>
                                <div className="cm-zone-card-stat">
                                  <FaLeaf size={9} />
                                  <span>{vegDensity}</span>
                                </div>
                              </div>
                            </div>

                            {showDrawer && (
                              <div className="cm-zone-inline-drawer" style={{ gridColumn: `1 / -1` }}>
                                {renderCellDetail(selectedCell, () => setSelectedCell(null), "inline")}
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>

                    {(() => {
                      const scores  = heatmap.map(c => c.cell_score || 0);
                      const ndvis   = heatmap.map(c => Number(c.ndvi) || 0);
                      const avg     = v => (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1);
                      const classCounts = heatmap.reduce((acc, c) => {
                        const k = normalizeHealthClass(c.health_class);
                        acc[k] = (acc[k] || 0) + 1;
                        return acc;
                      }, {});
                      const dominant = Object.entries(classCounts).sort((a, b) => b[1] - a[1])[0];
                      const domC = HEATMAP_COLORS[dominant?.[0]] || HEATMAP_COLORS.moderate_stress;
                      return (
                        <div className="cm-zone-summary-bar">
                          <div className="cm-zone-summary-item">
                            <span className="cm-zone-summary-label">Avg NDVI</span>
                            <strong className="cm-zone-summary-val">{avg(ndvis)}</strong>
                          </div>
                          <div className="cm-zone-summary-item">
                            <span className="cm-zone-summary-label">Avg Health</span>
                            <strong className="cm-zone-summary-val">{avg(scores)}%</strong>
                          </div>
                          <div className="cm-zone-summary-item">
                            <span className="cm-zone-summary-label">Best Zone</span>
                            <strong className="cm-zone-summary-val">
                              {getCellLabel(
                                heatmap.indexOf(heatmap.reduce((a, b) => (b.cell_score || 0) > (a.cell_score || 0) ? b : a)),
                                heatmap.length
                              )}
                            </strong>
                          </div>
                          <div className="cm-zone-summary-item">
                            <span className="cm-zone-summary-label">Worst Zone</span>
                            <strong className="cm-zone-summary-val">
                              {getCellLabel(
                                heatmap.indexOf(heatmap.reduce((a, b) => (b.cell_score || 0) < (a.cell_score || 0) ? b : a)),
                                heatmap.length
                              )}
                            </strong>
                          </div>
                          <div className="cm-zone-summary-item">
                            <span className="cm-zone-summary-label">Dominant Class</span>
                            <strong className="cm-zone-summary-val" style={{ color: domC.fill }}>
                              {domC.label} ({dominant?.[1]}/{heatmap.length})
                            </strong>
                          </div>
                          <div className="cm-zone-summary-item">
                            <span className="cm-zone-summary-label">Zones Needing Action</span>
                            <strong className="cm-zone-summary-val" style={{ color: "#ef4444" }}>
                              {heatmap.filter(c => ["critical","high_stress"].includes(normalizeHealthClass(c.health_class))).length} zones
                            </strong>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              {ndviAI && (
                <div className="cm-result-section">
                  <h3 className="cm-result-section-title">
                    <FaChartBar /> AI Model Analysis
                    <span className="cm-model-badge">RandomForest + GradientBoosting</span>
                  </h3>
                  <p className="cm-result-meta">{ndviAI.model_used}</p>

                  <div className="cm-ndvi-model-grid">
                    <div className="cm-ndvi-result-card">
                      <div className="cm-ndvi-result-label">Ensemble Prediction</div>
                      <div className="cm-ndvi-result-class" style={{
                        color: {
                          critical:"#ef4444", high_stress:"#f97316",
                          moderate_stress:"#eab308", good:"#84cc16", excellent:"#22c55e"
                        }[ndviAI.ensemble_class] || "#374151"
                      }}>
                        {ndviAI.ensemble_class?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}
                      </div>
                      <div className="cm-ndvi-result-score">{ndviAI.ensemble_score}% health</div>
                    </div>

                    <div className="cm-ndvi-result-card">
                      <div className="cm-ndvi-result-label">RandomForest Class</div>
                      <div className="cm-ndvi-result-class" style={{ color:"#2563eb" }}>
                        {ndviAI.ml_class?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}
                      </div>
                      <div className="cm-ndvi-result-score">{ndviAI.ml_confidence}% confidence</div>
                    </div>

                    <div className="cm-ndvi-result-card">
                      <div className="cm-ndvi-result-label">Physics Threshold</div>
                      <div className="cm-ndvi-result-class" style={{ color:"#7c3aed" }}>
                        {ndviAI.threshold_class?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}
                      </div>
                      <div className="cm-ndvi-result-score">Index-based rule</div>
                    </div>
                  </div>

                  {ndviAI.per_class_proba && (
                    <div className="cm-proba-section">
                      <div className="cm-proba-title">Class Probability Distribution</div>
                      {Object.entries(ndviAI.per_class_proba).map(([cls, pct]) => {
                        const barColor = {
                          critical:"#ef4444", high_stress:"#f97316",
                          moderate_stress:"#eab308", good:"#84cc16", excellent:"#22c55e"
                        }[cls] || "#9ca3af";
                        return (
                          <div key={cls} className="cm-proba-row">
                            <span className="cm-proba-label">{cls.replace(/_/g," ")}</span>
                            <div className="cm-proba-track">
                              <div className="cm-proba-fill" style={{ width:`${pct}%`, background: barColor }} />
                            </div>
                            <span className="cm-proba-pct">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {ndviAI.trend && (
                    <div className="cm-trend-box">
                      <div className="cm-trend-row">
                        <span>Zone / Season</span>
                        <strong>{ndviAI.trend.zone} &middot; {ndviAI.trend.season}</strong>
                      </div>
                      <div className="cm-trend-row">
                        <span>Expected</span>
                        <strong>{ndviAI.trend.expected_ndvi}</strong>
                      </div>
                      <div className="cm-trend-row">
                        <span>Actual</span>
                        <strong>{sat.ndvi}</strong>
                      </div>
                      <div className="cm-trend-row">
                        <span>Deviation</span>
                        <strong style={{ color: ndviAI.trend.deviation_pct >= 0 ? "#16a34a" : "#dc2626" }}>
                          {ndviAI.trend.deviation_pct > 0 ? "+" : ""}{ndviAI.trend.deviation_pct}%
                        </strong>
                      </div>
                      <div className="cm-trend-label">{ndviAI.trend.trend_label}</div>
                    </div>
                  )}
                </div>
              )}

              <div className="cm-result-section cm-recs-grid">
                <div>
                  <h3 className="cm-result-section-title"><FaTint /> Irrigation Recommendations</h3>
                  <ul className="cm-rec-list">
                    {(health.irrigation_recommendations || []).map((r, i) => (
                      <li key={i} className="cm-rec-item">
                        <FaCheckCircle className="cm-rec-icon" style={{ color: "#0ea5e9" }} />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="cm-result-section-title"><FaFlask /> Fertilizer Suggestions</h3>
                  <ul className="cm-rec-list">
                    {(health.fertilizer_suggestions || []).map((r, i) => (
                      <li key={i} className="cm-rec-item">
                        <FaCheckCircle className="cm-rec-icon" style={{ color: "#84cc16" }} />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="cm-result-section cm-risks-grid">
                <div>
                  <h3 className="cm-result-section-title"><FaExclamationTriangle /> Risk Alerts</h3>
                  <div className="cm-risk-list">
                    {(health.risk_alerts || []).map((alert, i) => (
                      <div key={i} className={`cm-risk-card cm-risk-${alert.level}`}>
                        <span className="cm-risk-icon">{alert.icon}</span>
                        <span className="cm-risk-msg">{alert.message}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="cm-result-section-title"><FaLeaf /> Analysis Summary</h3>
                  <div className="cm-summary-grid">
                    <div className="cm-summary-row"><span>Location</span><strong>{loc.name || "—"}</strong></div>
                    <div className="cm-summary-row"><span>Area Analysed</span><strong>{result.area_hectares ? `${result.area_hectares} ha` : "—"}</strong></div>
                    <div className="cm-summary-row"><span>Satellite Data Date</span><strong>{sat.acquisition_date || "—"}</strong></div>
                    <div className="cm-summary-row"><span>Analysis Time</span><strong>{result.analysis_timestamp ? new Date(result.analysis_timestamp).toLocaleString() : "—"}</strong></div>
                    <div className="cm-summary-row"><span>Data Source</span><strong>{sat.source || "Sentinel-2"}</strong></div>
                    <div className="cm-summary-row"><span>Images Used</span><strong>{sat.image_count ?? "—"}</strong></div>
                  </div>
                </div>
              </div>

            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CropMonitoringPage;
